import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Navigate } from 'react-router'

import { Icon, useFvNavigate } from '@fv/client-components'
import type { FullShipment, Quote } from '@fv/client-types'

import { Loading } from '@/components/Loading'
import { LoadSummary } from '@/components/LoadSummary/LoadSummary'
import { useRole } from '@/features/auth/hooks'
import { useUnifiedShipmentFlow } from '@/features/route-guides/hooks'
import { useCarrierBookSettings, useQueryParam } from '@/hooks/settings'
import { useQuoteRequestQuotes } from '@/hooks/shipments'
import { routes } from '@/routes'

import { useAddresses } from '../addresses/useAddress'
import { BookingForm } from './BookingForm'
import { BookingFormProvider } from './BookingFormProvider'

type Props = {
  loads: FullShipment[]
}

export const BookingPage = ({ loads }: Props) => {
  const [activeTruckIndex, setActiveTruckIndex] = useState(0)
  const navigate = useFvNavigate()
  const selected = useQueryParam('selected')
  const unifiedFlow = useUnifiedShipmentFlow()
  const role = useRole()

  const loadQuotes: { [keyof: string]: string } = JSON.parse(selected ?? '{}')
  const loadsToBook: Array<FullShipment & { quoteId: string }> = loads
    .filter(load => loadQuotes[load.loadId])
    .map(load => ({
      ...load,
      quoteId: loadQuotes[load.loadId],
    }))

  const addressQuery = useAddresses(loadsToBook[0]) // used inside booking form provider
  const quotesQuery = useQuoteRequestQuotes(loadsToBook)

  const selectedQuotes = loadsToBook
    .map(load => {
      const { loadId, quoteId } = load
      return quotesQuery.data[loadId]?.find(q => q._id === quoteId)
    })
    .filter((q): q is Quote => !!q)

  const carrierIds = selectedQuotes.map(q => q.carrierId)
  const bookSettingsQuery = useCarrierBookSettings('truckload', carrierIds)
  const firstLoad = loads[0]
  const isLoading =
    quotesQuery.isLoading || bookSettingsQuery.isLoading || addressQuery.loading

  useEffect(() => {
    if (quotesQuery.isLoading) return

    if (!loads.length) {
      toast.error('No shipments found for booking.', { id: 'BookingPage' })
      navigate('/pending')
      return
    }

    if (!selected) {
      toast.error('No quotes selected for booking.', { id: 'BookingPage' })
      navigate(routes.rates(firstLoad))
      return
    }

    if (!loadsToBook.length || !selectedQuotes.length) {
      toast.error('Selected quotes not found.', { id: 'BookingPage' })
      navigate(routes.rates(firstLoad))
      return
    }
  }, [
    firstLoad,
    loads.length,
    loadsToBook.length,
    navigate,
    quotesQuery.isLoading,
    selected,
    selectedQuotes.length,
  ])

  if (
    !loads.length ||
    !selected ||
    !loadsToBook.length ||
    !selectedQuotes.length
  ) {
    return null
  }

  const activeLoad = loadsToBook[activeTruckIndex]
  const activeQuote = selectedQuotes.find(q => q._id === activeLoad.quoteId)

  if (unifiedFlow.isLoading) return <Loading />

  // The unified page books one new truck; retenders and multi-truck bookings stay on this form.
  const isSingleNewBooking =
    loadsToBook.length === 1 && activeLoad.status === 'pending'

  if (
    unifiedFlow.isEnabled('truckload') &&
    (role === 'vendor' || isSingleNewBooking)
  ) {
    return (
      <Navigate
        to={routes.vendorBook(activeLoad.loadId, activeLoad.quoteId)}
        replace
      />
    )
  }

  return (
    <>
      <LoadSummary
        activeIndex={activeTruckIndex}
        equipmentType={activeQuote?.equipmentType}
        isBooking
        isEditingLoad={loadsToBook[0].status !== 'pending'}
        load={activeLoad}
        numberOfTrucks={loadsToBook.length}
        selectTruck={i => setActiveTruckIndex(i)}
      />

      <div className="flex min-h-0 min-w-0 flex-col h-full">
        {isLoading ? (
          <div className="m-auto flex flex-1 items-center justify-center">
            <Icon icon="spinner" size="2x" />
          </div>
        ) : (
          <BookingFormProvider
            loadsToBook={loadsToBook}
            selectedQuotes={selectedQuotes}
            settings={bookSettingsQuery.data}
            allQuotes={quotesQuery.data}
          >
            <BookingForm />
          </BookingFormProvider>
        )}
      </div>
    </>
  )
}
